import { useState, useMemo, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Shield,
  FolderOpen,
  Paperclip,
  Wrench,
  MessageSquare,
  Database,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  BudgetBar,
  CollapsibleSection,
  ContextBudgetPanel,
  ContextItemsList,
  FlatView,
  KBRetrievalsSection,
  MessagesList,
  RawJsonView,
} from '@/components/context'
import { useContextSnapshots, useContextSnapshotDetail, contextKeys } from '@/api/contextObservability'
import { useSignalR } from '@/hooks/useSignalR'
import { isSignalREnabled } from '@/lib/signalRService'
import { useQueryClient } from '@tanstack/react-query'
import { formatCompactNumber } from '@/lib/utils'
import type { ContextViewVariant } from '@/types/contextObservability'

export function ContextInspectorPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const [viewVariant, setViewVariant] = useState<ContextViewVariant>('inspector')
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const queryClient = useQueryClient()
  const signalREnabled = isSignalREnabled()

  const { data: snapshots, isLoading: isLoadingList } = useContextSnapshots(sessionId ?? '')
  const latestSnapshotId = snapshots?.[0]?.snapshotId ?? null
  const activeSnapshotId = selectedSnapshotId ?? latestSnapshotId

  const { data: snapshot, isLoading: isLoadingDetail } = useContextSnapshotDetail(activeSnapshotId)

  const handleContextSnapshot = useCallback(() => {
    if (sessionId) {
      queryClient.invalidateQueries({ queryKey: contextKeys.list(sessionId) })
    }
  }, [sessionId, queryClient])

  useSignalR({
    sessionId,
    enabled: signalREnabled && !!sessionId,
    onChatContextSnapshot: handleContextSnapshot,
  })

  const isLoading = isLoadingList || isLoadingDetail

  const snapshotOptions = useMemo(() =>
    (snapshots ?? []).map((s, idx) => ({
      id: s.snapshotId,
      label: `${new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · Turn ${snapshots!.length - idx} · ${formatCompactNumber(s.totalPromptTokens)} tok · ${s.toolCalls.length} tool call${s.toolCalls.length !== 1 ? 's' : ''}`,
    })),
    [snapshots]
  )

  if (isLoading) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-10 w-full" />
          <div className="grid grid-cols-[1fr_420px] gap-6">
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
            <Skeleton className="h-[400px] w-full" />
          </div>
        </div>
      </PageShell>
    )
  }

  if (!snapshot) {
    return (
      <PageShell>
        <div className="p-6 space-y-6">
          <PageHeader
            title={`Session ${sessionId?.slice(0, 8)}…`}
            backLink={`/sessions/${sessionId}`}
            backLabel="Back to Session"
          />
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Database className="h-12 w-12 mb-3 opacity-50" />
            <p className="text-sm">No context snapshots found for this session.</p>
            <p className="text-xs mt-1">Context snapshots are captured when the Context Observability feature is enabled.</p>
          </div>
        </div>
      </PageShell>
    )
  }

  const handleCopyPrompt = async () => {
    const text = JSON.stringify(snapshot, null, 2)
    await navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success('Snapshot JSON copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        <PageHeader
          title={`Session ${sessionId?.slice(0, 8)}…`}
          backLink={`/sessions/${sessionId}`}
          backLabel="Back to Session"
          description={
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>Agent: {snapshot.agent}</span>
              <span className="text-border">·</span>
              <span>Model: {snapshot.modelName}</span>
              <span className="text-border">·</span>
              <span>{new Date(snapshot.timestamp).toLocaleString()}</span>
              <span className="text-border">·</span>
              <span>{formatCompactNumber(snapshot.totalPromptTokens)} prompt + {formatCompactNumber(snapshot.totalCompletionTokens)} completion tokens</span>
              {snapshot.cacheHitRatio != null && (
                <>
                  <span className="text-border">·</span>
                  <span>Cache: {(snapshot.cacheHitRatio * 100).toFixed(0)}% hit</span>
                </>
              )}
            </div>
          }
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCopyPrompt}>
                {copied ? (
                  <Check className="h-4 w-4 mr-2 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                {copied ? 'Copied!' : 'Copy snapshot'}
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/sessions/${sessionId}`}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open session
                </Link>
              </Button>
            </div>
          }
        />

        {snapshotOptions.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Interaction:</span>
            <select
              value={activeSnapshotId ?? ''}
              onChange={(e) => setSelectedSnapshotId(e.target.value)}
              className="text-sm border rounded-md px-2 py-1 bg-background"
            >
              {snapshotOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        <BudgetBar
          sections={snapshot.sections}
          contextWindowSize={snapshot.contextWindowSize}
          summarizeThresholdPct={snapshot.summarizeThresholdPct}
        />

        <Tabs
          value={viewVariant}
          onValueChange={(v) => setViewVariant(v as ContextViewVariant)}
        >
          <TabsList>
            <TabsTrigger value="inspector">Inspector</TabsTrigger>
            <TabsTrigger value="flat">Flat bars</TabsTrigger>
            <TabsTrigger value="raw">Raw JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="inspector" className="mt-4">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
              <div className="space-y-3 min-w-0">
                <CollapsibleSection
                  sectionKey="system"
                  icon={<Shield className="h-4 w-4" />}
                  title="System Prompt"
                  tokens={snapshot.sections.find(s => s.key === 'system')?.tokens ?? 0}
                  contextWindowSize={snapshot.contextWindowSize}
                >
                  {snapshot.systemPromptContent ? (
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-muted p-3 rounded-md max-h-[70vh] overflow-y-auto">
                      {snapshot.systemPromptContent}
                    </pre>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      Full content not captured. Enable CaptureFullContent to see system prompt.
                    </p>
                  )}
                </CollapsibleSection>

                <CollapsibleSection
                  sectionKey="tools"
                  icon={<Wrench className="h-4 w-4" />}
                  title="Tool Definitions"
                  tokens={snapshot.sections.find(s => s.key === 'tools')?.tokens ?? 0}
                  contextWindowSize={snapshot.contextWindowSize}
                >
                  {snapshot.toolDefinitions.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {snapshot.toolDefinitions.map((tool) => (
                        <div
                          key={tool.name}
                          className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs bg-background"
                        >
                          <span className="font-mono font-medium">{tool.name}</span>
                          <span className="text-muted-foreground">{tool.tokens} tok</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No tool definitions</p>
                  )}
                </CollapsibleSection>

                <CollapsibleSection
                  sectionKey="messages"
                  icon={<MessageSquare className="h-4 w-4" />}
                  title="Conversation History"
                  tokens={snapshot.sections.find(s => s.key === 'messages')?.tokens ?? 0}
                  contextWindowSize={snapshot.contextWindowSize}
                  defaultOpen
                >
                  <MessagesList
                    messages={snapshot.messages}
                    toolCalls={snapshot.toolCalls}
                    summaryContent={snapshot.summaryContent}
                  />
                </CollapsibleSection>

                {snapshot.knowledgeRetrievals.length > 0 && (
                  <CollapsibleSection
                    sectionKey="session"
                    icon={<Database className="h-4 w-4" />}
                    title="KB Retrievals"
                    tokens={snapshot.knowledgeRetrievals.reduce(
                      (sum, r) => sum + r.results.reduce((s, res) => s + res.contentPreviewLength, 0),
                      0
                    )}
                    contextWindowSize={snapshot.contextWindowSize}
                    defaultOpen
                  >
                    <KBRetrievalsSection retrievals={snapshot.knowledgeRetrievals} />
                  </CollapsibleSection>
                )}

                <CollapsibleSection
                  sectionKey="session"
                  icon={<Paperclip className="h-4 w-4" />}
                  title="Session Context Items"
                  tokens={snapshot.sections.find(s => s.key === 'session')?.tokens ?? 0}
                  contextWindowSize={snapshot.contextWindowSize}
                >
                  <ContextItemsList items={snapshot.sessionContextItems} />
                </CollapsibleSection>

                <CollapsibleSection
                  sectionKey="project"
                  icon={<FolderOpen className="h-4 w-4" />}
                  title="Project Context"
                  tokens={snapshot.sections.find(s => s.key === 'project')?.tokens ?? 0}
                  contextWindowSize={snapshot.contextWindowSize}
                >
                  {Object.keys(snapshot.projectContext).length > 0 ? (
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                      {Object.entries(snapshot.projectContext).map(([key, value]) => (
                        <div key={key} className="contents">
                          <span className="text-muted-foreground">{key}</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No project context</p>
                  )}
                </CollapsibleSection>
              </div>

              <div className="hidden xl:block">
                <ContextBudgetPanel snapshot={snapshot} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="flat" className="mt-4">
            <FlatView
              sections={snapshot.sections}
              contextWindowSize={snapshot.contextWindowSize}
            />
          </TabsContent>

          <TabsContent value="raw" className="mt-4">
            <RawJsonView snapshot={snapshot} />
          </TabsContent>
        </Tabs>
      </div>
    </PageShell>
  )
}
